import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { Trash2, RotateCcw, Inbox } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { SkeletonListItem } from "../components/Skeleton";
import { useActionDialog } from "../components/ActionDialog";
import {
  getDeletedDeals,
  permanentlyDeleteDeal,
  restoreDeal,
  getAlldealforAdmin,
} from "../utils/firebasefunctions";
import { useStateValue } from "../context/StateProvider";
import { actionType } from "../context/reducer";
import {
  splitForPurge,
  isLeadBackingStub,
  RETENTION_DAYS,
  RETENTION_MS,
} from "../utils/retentionPurge";
import { canSeeDeal } from "../utils/globalSearch";

function formatCurrency(n) {
  const num = Number(n || 0);
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export default function RecentlyDeleted() {
  const [{ user }, dispatch] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      // Deals only. Deleted LEADS live in their own pipeline's Deleted tab.
      // Scoped with canSeeDeal — the same rule the Deals Board uses, so an
      // agent's trash holds the same files their board does.
      //
      // _key exists because a deal is identified by createdat, not by id.
      const dealRows = await getDeletedDeals();
      const items = dealRows
        // Hide live leads' hidden restore points (intake stubs and moved-deal
        // stubs). They are machinery, not human-deleted files, so they must not
        // appear here with a working Delete Forever button. isPurgeable also
        // refuses to auto-purge them, as a backstop. See retentionPurge.js.
        .filter((d) => canSeeDeal(d, user) && !isLeadBackingStub(d))
        .map((d) => ({ ...d, _kind: "deal", _key: String(d.createdat) }))
        .sort((a, b) => Number(b.deletedAt || 0) - Number(a.deletedAt || 0));
      // Auto-purge expired items (best-effort, runs whenever this page is
      // viewed). splitForPurge decides what may be destroyed — deals only, and
      // only with a sane positive timestamp. Every row here is a deal again,
      // but that guard stays: it is the safety rule, not a detail of who calls
      // it. See src/utils/retentionPurge.js.
      const { expired, kept } = splitForPurge(items, Date.now());
      for (const exp of expired) {
        try {
          await permanentlyDeleteDeal(exp.createdat);
        } catch (err) {
          console.warn("Auto-purge failed for", exp._key, err);
        }
      }
      setRows(kept);
    } catch (err) {
      console.error("Failed to load recently deleted:", err);
      toast.error("Could not load Recently Deleted");
    } finally {
      setLoading(false);
    }
  };

  // Wait for the user before loading: the rows are filtered through canSeeDeal,
  // and running before `user` lands would silently hide every deal (and never
  // retry, since this used to be a mount-only effect).
  useEffect(() => {
    if (!user?.id) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const syncDealsContext = async () => {
    try {
      const fresh = await getAlldealforAdmin("deals");
      dispatch({ type: actionType.STORE_DEALS, deals: fresh });
    } catch (err) {
      console.warn("Failed to refresh deals after restore/purge:", err);
    }
  };

  const handleRestore = async (deal) => {
    setBusyId(deal._key);
    try {
      const ok = await restoreDeal(deal.createdat);
      if (!ok) {
        toast.error("Could not restore deal");
        return;
      }
      toast.success("Deal restored");
      setRows((prev) => prev.filter((d) => d._key !== deal._key));
      syncDealsContext();
    } catch (err) {
      // Without this catch a thrown failure said nothing at all, leaving the
      // row on screen with no hint that the restore never happened.
      console.error("Restore failed:", err);
      toast.error("Could not restore deal");
    } finally {
      setBusyId(null);
    }
  };

  const handlePurge = async (deal) => {
    // In-app dialog, never window.confirm: Chrome can suppress native popups,
    // which makes this button silently do nothing.
    const ok = await confirm({
      title: "Delete permanently",
      body: `Permanently delete "${deal.propertyaddress || "this deal"}"?`,
      note: "This cannot be undone.",
      tone: "danger",
      confirmLabel: "Delete forever",
    });
    if (!ok) return;
    setBusyId(deal._key);
    try {
      const done = await permanentlyDeleteDeal(deal.createdat);
      if (!done) {
        toast.error("Could not delete deal");
        return;
      }
      toast.success("Deleted forever");
      setRows((prev) => prev.filter((d) => d._key !== deal._key));
    } catch (err) {
      // Same reason as handleRestore — and it matters more here: a silent
      // failure would leave the row on screen looking undeleted when it may
      // not be, or gone when it still exists.
      console.error("Permanent delete failed:", err);
      toast.error("Could not delete deal");
    } finally {
      setBusyId(null);
    }
  };

  const subtitle = useMemo(() => {
    if (loading) return "Loading…";
    if (rows.length === 0) return "Trash is empty";
    return `${rows.length} item${rows.length === 1 ? "" : "s"} · auto-purged after ${RETENTION_DAYS} days`;
  }, [rows.length, loading]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader title="Recently Deleted" subtitle={subtitle} />

      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6">
        {loading ? (
          <ul className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <li
                key={i}
                className="bg-white rounded-lg border border-slate-200"
              >
                <SkeletonListItem />
              </li>
            ))}
          </ul>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Inbox size={28} />
            <p className="text-sm mt-2">No deleted deals.</p>
            <p className="text-xs mt-1 text-slate-400">
              Deleted deals show up here for {RETENTION_DAYS} days. Deleted leads
              live in their pipeline's Deleted tab.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((deal) => {
              const purgeAt = (deal.deletedAt || 0) + RETENTION_MS;
              const daysLeft = Math.max(
                0,
                Math.ceil((purgeAt - Date.now()) / (24 * 60 * 60 * 1000)),
              );
              const busy = busyId === deal._key;
              return (
                <li
                  key={deal._key}
                  className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4"
                >
                  <img
                    src={
                      deal.propertyphoto?.length > 0
                        ? deal.propertyphoto[0]
                        : "/assets/no-image.png"
                    }
                    onError={(e) => {
                      e.currentTarget.src = "/assets/no-image.png";
                    }}
                    className="w-16 h-16 rounded-md object-cover flex-shrink-0 bg-slate-100"
                    alt=""
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 truncate">
                      {deal.propertyaddress || "Untitled deal"}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {deal.dealtype ? `${deal.dealtype} · ` : ""}
                      {formatCurrency(deal.offerPrice)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Deleted {moment(deal.deletedAt).fromNow()} ·{" "}
                      {daysLeft} day{daysLeft === 1 ? "" : "s"} left
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRestore(deal)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <RotateCcw size={14} />
                      Restore
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePurge(deal)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Delete forever
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {dialog}
    </div>
  );
}
